import React, { useMemo } from 'react';
import type { FormInstance } from 'antd';
import { Form, InputNumber, Popover, Select } from 'antd';
import FontSizeOutlined from '@ant-design/icons/FontSizeOutlined';
import FontColorsOutlined from '@ant-design/icons/FontColorsOutlined';
import AlignLeftOutlined from '@ant-design/icons/AlignLeftOutlined';
import ColumnHeightOutlined from '@ant-design/icons/ColumnHeightOutlined';
import BorderOutlined from '@ant-design/icons/BorderOutlined';
import BgColorsOutlined from '@ant-design/icons/BgColorsOutlined';
import ColumnWidthOutlined from '@ant-design/icons/ColumnWidthOutlined';
import LayoutOutlined from '@ant-design/icons/LayoutOutlined';
import TagOutlined from '@ant-design/icons/TagOutlined';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';
import { Tooltip } from 'antd';
import {
  RibbonDivider,
  RibbonGroup,
  RibbonPanel,
  WordStyleEditorShell,
} from '../bookDefaultStyles/WordStyleRibbon';
import { FieldLabel } from '../common/IconFormLabel';
import { StyleToggleButtons } from './StyleToggleButtons';
import { FontWeightToggleButtons, ItalicUnderlineToggles } from './HeaderStyleToggles';
import { AlignToggleButtons } from './AlignToggleButtons';
import { FontFamilySelect } from './FontFamilySelect';
import { ParagraphPositionControls } from './ParagraphPositionControls';
import { styleFieldName } from '../../utils/styleFormFieldNames';
import {
  BORDER_STYLE_OPTIONS,
  DISPLAY_OPTIONS,
  SPAN_OPTIONS,
  WIDTH_PERCENT_OPTIONS,
} from './styleEditorConstants';
import type { StyleTabItem } from './LineStyleEditCollapse';
import { RibbonControlHint } from './RibbonControlHint';
import { RibbonColorSwatch, toColorInputValue } from './RibbonColorSwatch';
import '../bookDefaultStyles/WordStyleRibbon.css';

const SPACING_FIELD_TITLES: Record<string, string> = {
  padding: 'Padding on all sides (px)',
  paddingLeft: 'Padding left (px)',
  paddingRight: 'Padding right (px)',
  paddingTop: 'Padding top (px)',
  paddingBottom: 'Padding bottom (px)',
  margin: 'Margin on all sides (px)',
  marginLeft: 'Margin left (px)',
  marginRight: 'Margin right (px)',
  marginTop: 'Margin top (px)',
  marginBottom: 'Margin bottom (px)',
};

const EXTRA_GROUP_META: Record<string, { title: string; icon: React.ReactNode }> = {
  image: { title: 'Picture', icon: <BgColorsOutlined /> },
  text: { title: 'Text', icon: <FontSizeOutlined /> },
  name: { title: 'Name', icon: <FontSizeOutlined /> },
};

export type TagEditRibbonConfig = {
  tags: string[];
  value: string;
  onChange: (value: string) => void;
  targetSummary?: string;
};

type Props = {
  form: FormInstance;
  namePrefix?: string | string[];
  mode?: 'line' | 'header';
  showSpan?: boolean;
  includeStyleSections?: boolean;
  extraItems?: StyleTabItem[];
  tagEdit?: TagEditRibbonConfig;
  disabled?: boolean;
};

/** Plain button for Popover triggers — must forward ref for Ant Design Popover. */
const RibbonTriggerButton = React.forwardRef<
  HTMLButtonElement,
  { title: string; className?: string; children: React.ReactNode }
>(({ title, className, children }, ref) => (
  <button
    ref={ref}
    type="button"
    aria-label={title}
    className={`word-ribbon-icon-btn${className ? ` ${className}` : ''}`}
  >
    {children}
  </button>
));
RibbonTriggerButton.displayName = 'RibbonTriggerButton';

function ColorPickerField({
  namePrefix,
  fieldKey,
  title,
  icon,
}: {
  namePrefix?: string | string[];
  fieldKey: 'color' | 'backgroundColor' | 'borderColor';
  title: string;
  icon?: React.ReactNode;
}) {
  const swatchIcon =
    icon ??
    (fieldKey === 'backgroundColor' ? (
      <BgColorsOutlined />
    ) : fieldKey === 'borderColor' ? (
      <BorderOutlined />
    ) : (
      <FontColorsOutlined />
    ));

  return (
    <Form.Item
      name={styleFieldName(namePrefix, fieldKey)}
      noStyle
      getValueProps={(value) => ({ value: toColorInputValue(value) })}
    >
      <RibbonColorSwatch title={title} icon={swatchIcon} />
    </Form.Item>
  );
}

function SpacingField({
  form,
  namePrefix,
  fieldKey,
  label,
}: {
  form: FormInstance;
  namePrefix?: string | string[];
  fieldKey: string;
  label: React.ReactNode;
}) {
  const field = styleFieldName(namePrefix, fieldKey);
  const value = Form.useWatch(field, form);

  const hint = SPACING_FIELD_TITLES[fieldKey] ?? String(fieldKey);

  return (
    <div className="word-ribbon-popover-field">
      <div className="word-ribbon-popover-field__label">{label}</div>
      <RibbonControlHint title={hint}>
        <InputNumber
          size="small"
          min={0}
          max={64}
          className="word-ribbon-compact-input"
          value={value}
          onChange={(v) => form.setFieldValue(field, v)}
        />
      </RibbonControlHint>
    </div>
  );
}

function SpacingPopoverGrid({
  form,
  namePrefix,
  kind,
}: {
  form: FormInstance;
  namePrefix?: string | string[];
  kind: 'padding' | 'margin';
}) {
  const keys =
    kind === 'padding'
      ? (['padding', 'paddingLeft', 'paddingRight', 'paddingTop', 'paddingBottom'] as const)
      : (['margin', 'marginLeft', 'marginRight', 'marginTop', 'marginBottom'] as const);

  const labels: Record<string, React.ReactNode> = {
    padding: FieldLabel.padding,
    paddingLeft: FieldLabel.paddingLeft,
    paddingRight: FieldLabel.paddingRight,
    paddingTop: FieldLabel.paddingTop,
    paddingBottom: FieldLabel.paddingBottom,
    margin: FieldLabel.margin,
    marginLeft: FieldLabel.marginLeft,
    marginRight: FieldLabel.marginRight,
    marginTop: FieldLabel.marginTop,
    marginBottom: FieldLabel.marginBottom,
  };

  return (
    <div className="word-ribbon-popover-grid">
      {keys.map((key) => (
        <SpacingField key={key} form={form} namePrefix={namePrefix} fieldKey={key} label={labels[key]} />
      ))}
    </div>
  );
}

function TagEditRibbonGroup({ tagEdit }: { tagEdit: TagEditRibbonConfig }) {
  const infoTitle = tagEdit.targetSummary ?? (tagEdit.value ? 'Tagged targets' : 'Pick a tag to enable style editing');

  return (
    <>
      <RibbonGroup title="Tag" icon={<TagOutlined />}>
        <div className="word-ribbon-inline">
          <RibbonControlHint title="Select a tag to style all matching content">
            <Select
              value={tagEdit.value || undefined}
              onChange={(v) => tagEdit.onChange(v ?? '')}
              allowClear
              placeholder="Select tag…"
              size="small"
              className="word-ribbon-tag-select"
              options={tagEdit.tags.map((t) => ({ label: t, value: t }))}
            />
          </RibbonControlHint>
          <Tooltip title={infoTitle}>
            <span className="word-ribbon-tag-info" aria-label={infoTitle}>
              <InfoCircleOutlined />
            </span>
          </Tooltip>
        </div>
      </RibbonGroup>
      <RibbonDivider />
    </>
  );
}

function RibbonEditableGroups({
  disabled,
  children,
}: {
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`word-ribbon-editable-groups${disabled ? ' word-ribbon-editable-groups--disabled' : ''}`}
      aria-disabled={disabled || undefined}
    >
      {children}
    </div>
  );
}

export const LineStyleWordRibbon: React.FC<Props> = ({
  form,
  namePrefix,
  mode = 'line',
  showSpan = false,
  includeStyleSections = true,
  extraItems = [],
  tagEdit,
  disabled = false,
}) => {
  const extraGroups = useMemo(
    () =>
      extraItems.map((item) => ({
        key: item.key,
        title: EXTRA_GROUP_META[item.key]?.title ?? 'Content',
        icon: EXTRA_GROUP_META[item.key]?.icon ?? <FontSizeOutlined />,
        children: item.children,
      })),
    [extraItems],
  );

  if (!includeStyleSections && extraGroups.length > 0) {
    return (
      <WordStyleEditorShell>
        <RibbonPanel>
          {tagEdit ? <TagEditRibbonGroup tagEdit={tagEdit} /> : null}
          <RibbonEditableGroups disabled={disabled}>
            {extraGroups.map((group, index) => (
              <React.Fragment key={group.key}>
                {index > 0 ? <RibbonDivider /> : null}
                <RibbonGroup title={group.title} icon={group.icon}>
                  {group.children}
                </RibbonGroup>
              </React.Fragment>
            ))}
          </RibbonEditableGroups>
        </RibbonPanel>
      </WordStyleEditorShell>
    );
  }

  const styleGroups = (
    <>
        {extraGroups.map((group, index) => (
          <React.Fragment key={group.key}>
            {index > 0 ? <RibbonDivider /> : null}
            <RibbonGroup title={group.title} icon={group.icon}>
              {group.children}
            </RibbonGroup>
          </React.Fragment>
        ))}
        {extraGroups.length > 0 ? <RibbonDivider /> : null}

        <RibbonGroup title="Font" icon={<FontSizeOutlined />}>
          <div className="word-ribbon-inline">
            <RibbonControlHint title="Font family">
              <Form.Item name={styleFieldName(namePrefix, 'fontFamily')} noStyle>
                <FontFamilySelect />
              </Form.Item>
            </RibbonControlHint>
            <RibbonControlHint title="Font size (px)">
              <Form.Item name={styleFieldName(namePrefix, 'fontSize')} rules={[{ required: true }]} noStyle>
                <InputNumber size="small" min={10} max={72} className="word-ribbon-compact-input word-ribbon-size-input" />
              </Form.Item>
            </RibbonControlHint>
            {mode === 'header' ? (
              <>
                <FontWeightToggleButtons form={form} namePrefix={namePrefix} />
                <ItalicUnderlineToggles form={form} namePrefix={namePrefix} />
              </>
            ) : (
              <StyleToggleButtons form={form} namePrefix={namePrefix} />
            )}
            <ColorPickerField namePrefix={namePrefix} fieldKey="color" title="Font color — text color" />
            <ColorPickerField namePrefix={namePrefix} fieldKey="backgroundColor" title="Highlight — text background color" />
          </div>
        </RibbonGroup>

        <RibbonDivider />

        <RibbonGroup title="Paragraph" icon={<AlignLeftOutlined />}>
          <div className="word-ribbon-inline word-ribbon-paragraph-row">
            <AlignToggleButtons form={form} namePrefix={namePrefix} />
            <ParagraphPositionControls form={form} namePrefix={namePrefix} />
          </div>
        </RibbonGroup>

        <RibbonDivider />

        <RibbonGroup title="Spacing" icon={<ColumnHeightOutlined />}>
          <Popover
            trigger="click"
            getPopupContainer={() => document.body}
            content={<SpacingPopoverGrid form={form} namePrefix={namePrefix} kind="padding" />}
          >
            <RibbonControlHint title="Padding — space inside the border">
              <RibbonTriggerButton title="Padding — space inside the border">
                <ColumnHeightOutlined />
              </RibbonTriggerButton>
            </RibbonControlHint>
          </Popover>
          <Popover
            trigger="click"
            getPopupContainer={() => document.body}
            content={<SpacingPopoverGrid form={form} namePrefix={namePrefix} kind="margin" />}
          >
            <RibbonControlHint title="Margin — space outside the border">
              <RibbonTriggerButton title="Margin — space outside the border">
                <ColumnHeightOutlined style={{ transform: 'scaleY(-1)' }} />
              </RibbonTriggerButton>
            </RibbonControlHint>
          </Popover>
        </RibbonGroup>

        <RibbonDivider />

        <RibbonGroup title="Layout" icon={<LayoutOutlined />}>
          <div className="word-ribbon-inline">
            {showSpan ? (
              <RibbonControlHint title="Column width — half or full row">
                <Form.Item name={styleFieldName(namePrefix, 'span')} initialValue={24} noStyle>
                  <Select
                    size="small"
                    className="word-ribbon-compact-select"
                    style={{ width: 72 }}
                    options={SPAN_OPTIONS}
                  />
                </Form.Item>
              </RibbonControlHint>
            ) : null}
            <RibbonControlHint title="Content width — percentage of available space">
              <Form.Item name={styleFieldName(namePrefix, 'widthPercent')} initialValue={0} noStyle>
                <Select
                  size="small"
                  className="word-ribbon-compact-select"
                  style={{ width: 72 }}
                  options={WIDTH_PERCENT_OPTIONS}
                />
              </Form.Item>
            </RibbonControlHint>
            <RibbonControlHint title="Display mode — block, inline, or inline-block">
              <Form.Item name={styleFieldName(namePrefix, 'display')} initialValue="" noStyle>
                <Select
                  size="small"
                  className="word-ribbon-compact-select"
                  style={{ width: 96 }}
                  options={DISPLAY_OPTIONS}
                />
              </Form.Item>
            </RibbonControlHint>
          </div>
        </RibbonGroup>

        <RibbonDivider />

        <RibbonGroup title="Borders" icon={<BorderOutlined />}>
          <div className="word-ribbon-inline">
            <RibbonControlHint title="Border width (px)">
              <Form.Item name={styleFieldName(namePrefix, 'borderWidth')} noStyle>
                <InputNumber size="small" min={0} max={12} className="word-ribbon-compact-input" placeholder="W" />
              </Form.Item>
            </RibbonControlHint>
            <RibbonControlHint title="Border style — solid, dashed, dotted…">
              <Form.Item name={styleFieldName(namePrefix, 'borderStyle')} noStyle>
                <Select size="small" className="word-ribbon-compact-select" style={{ width: 88 }} options={BORDER_STYLE_OPTIONS} />
              </Form.Item>
            </RibbonControlHint>
            <ColorPickerField namePrefix={namePrefix} fieldKey="borderColor" title="Border color" />
            <RibbonControlHint title="Border corner radius (px)">
              <Form.Item name={styleFieldName(namePrefix, 'borderRadius')} noStyle>
                <InputNumber size="small" min={0} max={24} className="word-ribbon-compact-input" placeholder="R" />
              </Form.Item>
            </RibbonControlHint>
          </div>
        </RibbonGroup>
    </>
  );

  return (
    <WordStyleEditorShell>
      <RibbonPanel>
        {tagEdit ? <TagEditRibbonGroup tagEdit={tagEdit} /> : null}
        <RibbonEditableGroups disabled={disabled}>{styleGroups}</RibbonEditableGroups>
      </RibbonPanel>
    </WordStyleEditorShell>
  );
};
